/**
 * Your Journey's wording for an upcoming interview's time (EN-1030), in the viewer's own zone: the
 * API sends `scheduled_at` in UTC, and the user scheduled it in their local time. Also the link that
 * opens InterviewAI prefilled for it (EN-1038).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

// Current ICU puts a narrow no-break space before AM/PM; the copy uses a plain one.
export const format = (date, options, timeZone) => new Intl.DateTimeFormat('en-US', { ...options, timeZone })
	.format(date)
	.replace(/\u202f/g, ' ');

// The calendar day a moment falls on in the zone, as a UTC midnight, so two days subtract cleanly.
export const calendarDay = (date, timeZone) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
		timeZone,
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
	}).formatToParts(date).map(({ type, value }) => [type, Number(value)]));

	return Date.UTC(parts.year, parts.month - 1, parts.day);
};

const RELATIVE_DAYS = ['Today', 'Tomorrow'];

/**
 * `when` completes "Your {Company} interview is …": `today`, `tomorrow`, `on Thursday` up to six
 * days out, else `on Oct 21`, where a weekday would be ambiguous. `full` is the body's
 * `Thursday, Sep 17 at 2:00 PM`, and `short` the header button's `Thu, 2:00 PM`, `Today, 2:00 PM` or
 * `Oct 21, 2:00 PM`.
 *
 * `now` and `timeZone` are for tests; the widget uses the browser's clock and zone.
 */
export const interviewTime = (scheduledAt, { now = new Date(), timeZone } = {}) => {
	const date = new Date(scheduledAt);
	const days = Math.round((calendarDay(date, timeZone) - calendarDay(now, timeZone)) / DAY_MS);
	const time = format(date, { hour: 'numeric', minute: '2-digit' }, timeZone);
	const monthDay = format(date, { month: 'short', day: 'numeric' }, timeZone);
	const weekday = format(date, { weekday: 'long' }, timeZone);
	const full = `${weekday}, ${monthDay} at ${time}`;
	const relative = RELATIVE_DAYS[days];

	if (relative) {
		return { when: relative.toLowerCase(), full, short: `${relative}, ${time}` };
	}

	if (days < 7) {
		return {
			when: `on ${weekday}`,
			full,
			short: `${format(date, { weekday: 'short' }, timeZone)}, ${time}`,
		};
	}

	return { when: `on ${monthDay}`, full, short: `${monthDay}, ${time}` };
};

/**
 * InterviewAI's create flow prefilled for the interview (EN-1038). It skips Choose a Job when the
 * resume is there, and loads the job description from the tracker row. Without a primary resume,
 * Choose a Job opens with the job filled in (EN-1030).
 */
export const practiceLink = ({ job_title: title, company, application, resume }) => ({
	name: 'interview-ai-create',
	query: { title, company, jd: application, ...(resume ? { resume } : {}) },
});
